import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Flex,
  Heading,
  IconBox,
  Link,
  Text,
  TextField,
} from '@/components/ui';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  IconAlertTriangle,
  IconEye,
  IconEyeOff,
  IconLock,
  IconLockAccess,
  IconUser,
} from '@tabler/icons-react';

import Logo from '@/components/Logo';
import LogoMark from '@/components/LogoMark';
import Copyrights from '@/components/Copyrights';
import { ROUTES } from '@/config/routes';
import { useLogin } from '@/services/authHooks';
import { useAuth } from '@/contexts/AuthContext';
import { useCustomPageMeta } from '@/hooks/usePageMeta';
import LoginImage from './login-bg.png';

interface LoginPageProps {
  redirectOnSuccess?: boolean;
  nextOverride?: string | null;
  embedded?: boolean;
}

export const LoginPage: React.FC<LoginPageProps> = ({
  redirectOnSuccess = true,
  nextOverride = null,
  embedded = false,
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const next = nextOverride ?? searchParams?.get('next');
  const { user, status } = useAuth();
  const loginRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formErrors, setFormErrors] = useState<{
    login?: string;
    password?: string;
  }>({});
  const { mutate: login, isPending } = useLogin();

  useCustomPageMeta({
    title: 'Sign In - Syncrony Data Uploader',
    description:
      'Secure login to access your clinical research data management platform',
    keywords: [
      'login',
      'sign in',
      'authentication',
      'secure access',
      'syncrony',
    ],
    ogTitle: 'Sign In - Syncrony Data Uploader',
    ogDescription:
      'Secure login to access your clinical research data management platform',
  });

  const getSafeNextPath = (value: string | null) => {
    if (!value) return null;
    if (!value.startsWith('/')) return null;
    if (value.startsWith('//')) return null;
    if (value.startsWith('/login')) return null;
    return value;
  };

  const safeNext = getSafeNextPath(next);

  // Redirect after successful login
  useEffect(() => {
    if (!redirectOnSuccess) return;
    if (status === 'success' && user && user.id) {
      if (safeNext) {
        navigate(safeNext, { replace: true });
      } else {
        navigate(ROUTES.dashboard, { replace: true });
      }
    }
  }, [user, status, navigate, safeNext, redirectOnSuccess]);

  // Clear errors when user starts typing
  const handleInputChange = () => {
    setError(null);
    setFormErrors({});
  };

  const validateForm = () => {
    const errors: { login?: string; password?: string } = {};
    const loginValue = loginRef.current?.value || '';
    const password = passwordRef.current?.value || '';

    if (!loginValue) {
      errors.login = 'Username or email is required';
    } else if (loginValue.length < 3) {
      errors.login = 'Please enter a valid username or email address';
    }

    if (!password) {
      errors.password = 'Password is required';
    } else if (password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setFormErrors({});

    if (!validateForm()) {
      return;
    }

    const loginValue = loginRef.current?.value || '';
    const password = passwordRef.current?.value || '';

    login(
      { login: loginValue, password },
      {
        onSuccess: () => {
          if (!redirectOnSuccess) return;
          // Redirect immediately after successful login
          if (safeNext) {
            navigate(safeNext, { replace: true });
          } else {
            navigate(ROUTES.dashboard, { replace: true });
          }
        },
        onError: (err: unknown) => {
          if (isErrorWithMessage(err)) {
            // Handle Password expired error specifically
            if (err.message.includes('Password expired')) {
              setError(err.message);
            } else {
              setError(err.message);
            }
          } else {
            setError('Login failed. Please try again.');
          }
        },
      }
    );
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const content = (
    <div className="app-layout__auth--container">
      <div className="left">
        <Flex style={{ color: 'var(--primary-contrast)', fontSize: '1.4rem' }}>
          <img className="login-image" src={LoginImage} alt="" />
          <Logo />
        </Flex>
      </div>
      <Card className="right">
        <Flex my="auto" py="6" className="app-layout__auth--card" asChild>
          <form onSubmit={handleSubmit}>
            <CardContent>
              <Flex direction="column" gap="2" mt="4" mb="4" align="center">
                <IconBox
                  style={{ margin: 'var(--space-4) auto var(--space-8) auto)' }}
                >
                  <LogoMark />
                </IconBox>
                <Heading style={{ marginTop: '1rem' }}>Welcome Back!</Heading>
                <Text size="xsmall">Please log in to access your account.</Text>
              </Flex>

              <Flex direction="column" gap="4" mt="4" mb="4">
                <TextField
                  label="Username or Email"
                  type="text"
                  name="login"
                  required
                  placeholder="Enter your username or email"
                  startSlot={<IconUser />}
                  ref={loginRef}
                  disabled={isPending}
                  error={!!formErrors.login}
                  errorMessage={formErrors.login}
                  onChange={handleInputChange}
                />

                <TextField
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  required
                  placeholder="Enter your password"
                  ref={passwordRef}
                  disabled={isPending}
                  error={!!formErrors.password}
                  errorMessage={formErrors.password}
                  onChange={handleInputChange}
                  startSlot={<IconLock />}
                  endSlot={
                    <Button
                      onClick={togglePasswordVisibility}
                      isIconOnly
                      variant="ghost"
                      size="small"
                      disabled={isPending}
                      style={{ marginRight: 'calc(var(--space-2) * -1)' }}
                    >
                      {showPassword ? <IconEyeOff /> : <IconEye />}
                    </Button>
                  }
                />
              </Flex>

              {/* Password Expired Error Alert */}
              {error && error.includes('Password expired') && (
                <Alert
                  color="error"
                  icon={<IconLock />}
                  title="Password Expired"
                  description={
                    <span>
                      Please reset your password at
                      <Link
                        ml="1"
                        href="https://syncronyedc.com"
                        target="_blank"
                        highContrast
                        color="crimson"
                        underline="hover"
                        rel="noopener noreferrer"
                        weight="bold"
                        style={{
                          color: 'inherit',
                        }}
                      >
                        https://syncronyedc.com
                      </Link>
                    </span>
                  }
                  className="mb-4"
                />
              )}

              {/* 403 Error Alert */}
              {error && error.includes('Access denied') && (
                <Alert
                  color="error"
                  variant="surface"
                  icon={<IconAlertTriangle />}
                  title="Access Denied"
                  description={error}
                  className="mb-4"
                />
              )}

              {/* General error message */}
              {error &&
                !error.includes('Access denied') &&
                !error.includes('Password expired') && (
                  <Alert
                    icon={<IconLockAccess />}
                    size="small"
                    color="error"
                    style={{ marginTop: 8 }}
                    title="Login Failed"
                    description={error}
                  />
                )}
              <Button
                type="submit"
                color="primary"
                variant="classic"
                mt="5"
                fullWidth
                loading={isPending}
              >
                Sign In
              </Button>
            </CardContent>
          </form>
        </Flex>
        <Copyrights />
      </Card>
    </div>
  );

  if (embedded) {
    return <div className="app-layout__auth">{content}</div>;
  }

  return content;
};

// Type guard for error with message
function isErrorWithMessage(error: unknown): error is { message: string } {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as { message: unknown }).message === 'string'
  );
}
